/**
 * Funnel client unique en 6 etapes, toujours dans le meme ordre :
 *
 * 1. Pack          — on demarre par choisir l'offre
 * 2. Qualification — type de demande + champs specifiques
 * 3. Informations  — adresse + contact + donnees techniques
 * 4. Fibre         — questions fibre
 * 5. Documents     — depot avec checklist selon le pack
 * 6. Paiement      — vaut soumission automatique du dossier
 *
 * Pour chaque etape, on retourne :
 *  - `status` : 'done' / 'current' / 'todo'
 *  - `key`    : identifiant stable pour la cle React
 *  - `label`  : libelle affiche
 *
 * On expose aussi :
 *  - `nextActionTarget(project, documentsCount, paymentDone)` : URL ou ancre
 *    vers la prochaine action concrete a effectuer.
 *  - `resolveInitialStep(project, documentsCount, paymentDone)` : step de
 *    reprise dans le wizard / hub.
 */
import type { Project, ProjectStatus } from '@bcl/shared';

export type BookingStepKey =
  | 'pack'
  | 'qualification'
  | 'info'
  | 'fiber'
  | 'documents'
  | 'payment';

export type BookingStepStatus = 'done' | 'current' | 'todo';

export interface BookingStep {
  key: BookingStepKey;
  label: string;
  status: BookingStepStatus;
}

const SUBMITTED_STATUSES: ProjectStatus[] = [
  'en_verification_bcl',
  'dossier_valide',
  'paiement_valide',
  'pris_en_charge',
  'analyse_en_cours',
  'previsite_planifiee',
  'intervention_realisee',
  'compte_rendu_disponible',
  'travaux_en_cours',
  'termine',
  'cloture',
];

/**
 * Indique si le dossier a ete soumis a verification BCL (post-paiement).
 */
export function isProjectSubmitted(project: Project): boolean {
  return SUBMITTED_STATUSES.includes(project.status);
}

const STEP_ORDER: BookingStepKey[] = [
  'pack',
  'qualification',
  'info',
  'fiber',
  'documents',
  'payment',
];

const STEP_LABELS: Record<BookingStepKey, string> = {
  pack: 'Choix du pack',
  qualification: 'Qualification',
  info: 'Informations projet',
  fiber: 'Questions fibre',
  documents: 'Dépôt documents',
  payment: 'Paiement',
};

export function computeBookingSteps(
  project: Project,
  options: { documentsCount: number; paymentDone: boolean },
): BookingStep[] {
  const flags: Record<BookingStepKey, boolean> = {
    pack: project.packCode !== null,
    qualification: project.qualificationAnswers !== null,
    info: project.info !== null,
    fiber: project.fiberQuestions !== null,
    documents: options.documentsCount > 0,
    payment: options.paymentDone,
  };

  // Le premier step non-fait devient "current", les suivants restent "todo".
  let currentAssigned = false;
  return STEP_ORDER.map((key) => {
    if (flags[key]) {
      return { key, label: STEP_LABELS[key], status: 'done' as const };
    }
    if (!currentAssigned) {
      currentAssigned = true;
      return { key, label: STEP_LABELS[key], status: 'current' as const };
    }
    return { key, label: STEP_LABELS[key], status: 'todo' as const };
  });
}

/**
 * Progression du dossier en pourcent (0..100) calculee uniquement a partir
 * du payload du projet — pas besoin de fetcher documents/payments en plus.
 *
 * Heuristique :
 *  - Chacune des 6 etapes du funnel pese 14% (Pack, Qualif, Info, Fibre,
 *    Documents, Paiement).
 *  - On considere « Documents » comme atteint des que le status BDD est
 *    `documents_deposes` ou plus avance.
 *  - On considere « Paiement » comme atteint des que le status BDD est
 *    `paiement_valide` ou plus avance.
 *  - 100% des qu'on est passe en `paiement_valide` (le reste est suivi
 *    de production cote BCL, deja represente par le statut affiche).
 */
export function computePayloadProgressPercent(project: Project): number {
  const STATUS_FROM_DOCS: ProjectStatus[] = [
    'documents_deposes',
    'en_verification_bcl',
    'dossier_valide',
    'paiement_valide',
    'pris_en_charge',
    'analyse_en_cours',
    'previsite_planifiee',
    'intervention_realisee',
    'compte_rendu_disponible',
    'travaux_en_cours',
    'termine',
    'cloture',
  ];
  const STATUS_PAID: ProjectStatus[] = [
    'paiement_valide',
    'pris_en_charge',
    'analyse_en_cours',
    'previsite_planifiee',
    'intervention_realisee',
    'compte_rendu_disponible',
    'travaux_en_cours',
    'termine',
    'cloture',
  ];
  const flags = [
    project.packCode !== null,
    project.qualificationAnswers !== null,
    project.info !== null,
    project.fiberQuestions !== null,
    STATUS_FROM_DOCS.includes(project.status),
    STATUS_PAID.includes(project.status),
  ];
  const done = flags.filter(Boolean).length;
  // 6 etapes -> chacune pese 100/6, on arrondit a l'entier le plus proche.
  return Math.round((done / flags.length) * 100);
}

/**
 * Cible (URL absolue ou ancre `#section`) de la prochaine action concrete
 * attendue de l'utilisateur. Si toutes les etapes sont terminees, renvoie
 * `null` (le bouton CTA est masque).
 */
export function nextActionTarget(
  project: Project,
  options: { documentsCount: number; paymentDone: boolean },
): { href: string; label: string } | null {
  if (project.packCode === null) {
    return {
      href: `/projects/${project.id}/wizard`,
      label: 'Choisir mon pack',
    };
  }
  if (project.qualificationAnswers === null) {
    return {
      href: `/projects/${project.id}/wizard`,
      label: 'Qualifier ma demande',
    };
  }
  if (project.info === null) {
    return {
      href: `/projects/${project.id}/wizard`,
      label: 'Compléter les informations',
    };
  }
  if (project.fiberQuestions === null) {
    return {
      href: `/projects/${project.id}/wizard`,
      label: 'Répondre aux questions fibre',
    };
  }
  if (options.documentsCount === 0) {
    return { href: '#documents', label: 'Déposer mes documents' };
  }
  if (!options.paymentDone) {
    return { href: '#paiement', label: 'Régler mon dossier' };
  }
  return null;
}

/**
 * Step initial du wizard selon ce qui a deja ete renseigne. Si tout est rempli
 * et que le paiement est ok, on retourne 'done' — le ProjectWizardPage redirige
 * alors vers /projects/:id.
 *
 * Le funnel demarre TOUJOURS par le pack : si packCode est null, on renvoie
 * 'pack', meme si d'autres champs etaient deja renseignes.
 */
export type WizardStepKey =
  | 'pack'
  | 'qualification'
  | 'info'
  | 'fiber'
  | 'documents'
  | 'payment'
  | 'done';

export function resolveInitialStep(
  project: Project,
  options: { documentsCount: number; paymentDone: boolean } = {
    documentsCount: 0,
    paymentDone: false,
  },
): WizardStepKey {
  if (project.packCode === null) return 'pack';
  if (project.qualificationAnswers === null) return 'qualification';
  if (project.info === null) return 'info';
  if (project.fiberQuestions === null) return 'fiber';
  if (options.documentsCount === 0) return 'documents';
  if (!options.paymentDone) return 'payment';
  return 'done';
}
